import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useLanguage, LANGUAGES } from "@/i18n/LanguageContext";

const LangSwitcher = ({ compact = false }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div className="flex items-center gap-0.5 rounded-md border border-slate-200 bg-white p-0.5" data-testid="language-switcher">
      {LANGUAGES.map((l) => (
        <button
          key={l.id}
          onClick={() => setLang(l.id)}
          data-testid={`lang-switch-${l.id}`}
          className={`rounded px-2 py-1 font-mono text-[11px] font-semibold tracking-wider transition-colors duration-200 ${
            lang === l.id ? "bg-sky-600 text-white" : "text-slate-500 hover:text-sky-700"
          } ${compact ? "px-2.5 py-1.5" : ""}`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
};

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();

  const links = [
    { label: t("nav.home"), href: "#hjem", testid: "nav-link-home" },
    { label: t("nav.projects"), href: "#prosjekter", testid: "nav-link-projects" },
    { label: t("nav.services"), href: "#tjenester", testid: "nav-link-services" },
    { label: t("nav.office"), href: "#kontor", testid: "nav-link-carousel" },
    { label: t("nav.about"), href: "#om-oss", testid: "nav-link-about" },
    { label: t("nav.contact"), href: "#kontakt", testid: "nav-link-contact" },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur-md" data-testid="main-navbar">
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-8">
        <Link to="/admin" className="flex items-center gap-3" data-testid="nav-logo" title="Admin-innlogging">
          <img src="/logo-mark.png" alt="Norgesconsult logo" className="h-11 w-auto object-contain" />
          <span className="leading-tight">
            <span className="block font-display text-base font-bold uppercase tracking-[0.24em] text-slate-900 sm:text-lg">
              Norgesconsult
            </span>
            <span className="block text-[11px] font-medium uppercase tracking-[0.2em] text-slate-500">
              {t("nav.subtitle")}
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-6 lg:flex" data-testid="nav-desktop-menu">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              className="text-sm font-medium text-slate-600 transition-colors duration-200 hover:text-sky-700"
            >
              {l.label}
            </a>
          ))}
          <a
            href="tel:+4741247400"
            data-testid="nav-phone-link"
            className="hidden items-center gap-2 text-sm font-semibold text-slate-700 xl:flex"
          >
            <Phone size={15} className="text-sky-600" />
            +47 412 47 400
          </a>
          <LangSwitcher />
          <a
            href="#kontakt"
            data-testid="nav-cta-quote"
            className="rounded-md bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-sky-700"
          >
            {t("nav.cta")}
          </a>
        </nav>

        <button
          className="text-slate-700 lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Åpne meny"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <nav className="border-t border-slate-200 bg-white px-4 py-4 lg:hidden" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`mobile-${l.testid}`}
              onClick={() => setOpen(false)}
              className="block py-3 text-sm font-medium text-slate-700 transition-colors hover:text-sky-700"
            >
              {l.label}
            </a>
          ))}
          <div className="py-3">
            <LangSwitcher compact />
          </div>
          <a
            href="#kontakt"
            data-testid="mobile-nav-cta-quote"
            onClick={() => setOpen(false)}
            className="mt-2 block rounded-md bg-sky-600 px-5 py-3 text-center text-sm font-semibold text-white"
          >
            {t("nav.cta")}
          </a>
        </nav>
      )}
    </header>
  );
};

export default Navbar;
